// clientIdentifier.ts
// Utility for generating and managing a persistent client identifier with device fingerprinting

const CLIENT_ID_KEY = 'rb_client_id';
const DEVICE_FINGERPRINT_KEY = 'rb_device_fingerprint';

interface DeviceFingerprint {
    clientId: string;
    screenResolution: string;
    timezone: string;
    language: string;
    platform: string;
    hardwareConcurrency: number;
    deviceMemory?: number;
    colorDepth: number;
    pixelRatio: number;
    touchSupport: boolean;
    vendor: string;
    browserFingerprint: string;
    canvasFingerprint?: string;
    webglFingerprint?: string;
    audioFingerprint?: string;
    fonts?: string;
}

/**
 * Canvas fingerprinting - different GPUs/drivers render slightly differently
 */
const getCanvasFingerprint = (): string => {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 50;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.warn('Canvas 2D context not available');
            return 'no-canvas-context';
        }
        
        // Draw text with specific styling
        ctx.textBaseline = 'top';
        ctx.font = '14px "Arial"';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('Rockbridge Security', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Rockbridge Security', 4, 17);
        
        // Get image data and hash it
        const dataURL = canvas.toDataURL();
        return simpleHash(dataURL);
    } catch (e) {
        console.warn('Canvas fingerprinting failed:', e);
        return 'canvas-error';
    }
};

/**
 * WebGL fingerprinting - captures GPU and driver information
 */
const getWebGLFingerprint = (): string => {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
        
        if (!gl) {
            console.warn('WebGL not available');
            return 'no-webgl';
        }
        
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo) {
            console.warn('WEBGL_debug_renderer_info extension not available');
            // Fallback: use basic WebGL parameters
            const vendor = gl.getParameter(gl.VENDOR);
            const renderer = gl.getParameter(gl.RENDERER);
            return simpleHash(`${vendor}|${renderer}`);
        }
        
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        
        return simpleHash(`${vendor}|${renderer}`);
    } catch (e) {
        console.warn('WebGL fingerprinting failed:', e);
        return 'webgl-error';
    }
};

/**
 * Audio fingerprinting - different hardware produces slightly different audio characteristics
 * Uses modern Web Audio API without deprecated ScriptProcessorNode
 */
const getAudioFingerprint = async (): Promise<string> => {
    try {
        const AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AudioContext) {
            console.warn('AudioContext not available');
            return 'no-audio-context';
        }
        
        const context = new AudioContext();
        const oscillator = context.createOscillator();
        const analyser = context.createAnalyser();
        const gainNode = context.createGain();
        
        // Configure analyser
        analyser.fftSize = 2048;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        
        gainNode.gain.value = 0; // Mute
        oscillator.type = 'triangle';
        oscillator.frequency.value = 10000;
        
        oscillator.connect(analyser);
        analyser.connect(gainNode);
        gainNode.connect(context.destination);
        
        oscillator.start(0);
        
        return new Promise((resolve) => {
            // Wait a moment for the oscillator to stabilize
            setTimeout(() => {
                analyser.getByteFrequencyData(dataArray);
                const hash = simpleHash(Array.from(dataArray.slice(0, 100)).join(','));
                oscillator.stop();
                context.close();
                resolve(hash);
            }, 100);
        });
    } catch (e) {
        console.warn('Audio fingerprinting failed:', e);
        return 'audio-error';
    }
};

/**
 * Font fingerprinting - list of installed fonts is unique per system
 */
const getFontFingerprint = (): string => {
    try {
        const baseFonts = ['monospace', 'sans-serif', 'serif'];
        const testFonts = [
            'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia', 'Palatino',
            'Garamond', 'Comic Sans MS', 'Trebuchet MS', 'Impact', 'Tahoma'
        ];
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.warn('Canvas context not available for font fingerprinting');
            return 'no-canvas-for-fonts';
        }
        
        const testString = 'mmmmmmmmmmlli';
        const textSize = '72px';
        
        const baseFontWidths: Record<string, number> = {};
        baseFonts.forEach(baseFont => {
            ctx.font = `${textSize} ${baseFont}`;
            baseFontWidths[baseFont] = ctx.measureText(testString).width;
        });
        
        const availableFonts: string[] = [];
        testFonts.forEach(font => {
            baseFonts.forEach(baseFont => {
                ctx.font = `${textSize} ${font}, ${baseFont}`;
                const width = ctx.measureText(testString).width;
                if (width !== baseFontWidths[baseFont]) {
                    if (!availableFonts.includes(font)) {
                        availableFonts.push(font);
                    }
                }
            });
        });
        
        return simpleHash(availableFonts.join(','));
    } catch (e) {
        console.warn('Font fingerprinting failed:', e);
        return 'font-error';
    }
};

/**
 * Generates a device fingerprint based on browser and hardware characteristics
 * This creates a semi-unique identifier for the device
 */
export const generateDeviceFingerprint = async (): Promise<DeviceFingerprint> => {
    const clientId = getOrCreateClientId();
    
    // Gather device characteristics
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;
    const platform = navigator.platform;
    const hardwareConcurrency = navigator.hardwareConcurrency || 0;
    const deviceMemory = (navigator as any).deviceMemory; // Not available in all browsers
    const colorDepth = window.screen.colorDepth;
    const pixelRatio = window.devicePixelRatio;
    const touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const vendor = navigator.vendor;
    
    // Advanced fingerprinting
    const canvasFingerprint = getCanvasFingerprint();
    const webglFingerprint = getWebGLFingerprint();
    const audioFingerprint = await getAudioFingerprint();
    const fonts = getFontFingerprint();
    
    // Create a composite fingerprint hash
    const fingerprintData = [
        screenResolution,
        timezone,
        language,
        platform,
        hardwareConcurrency,
        colorDepth,
        pixelRatio,
        touchSupport,
        vendor,
        canvasFingerprint,
        webglFingerprint,
        audioFingerprint,
        fonts
    ].join('|');
    
    // Simple hash function for the fingerprint
    const browserFingerprint = simpleHash(fingerprintData);
    
    const fingerprint: DeviceFingerprint = {
        clientId,
        screenResolution,
        timezone,
        language,
        platform,
        hardwareConcurrency,
        deviceMemory,
        colorDepth,
        pixelRatio,
        touchSupport,
        vendor,
        browserFingerprint,
        canvasFingerprint,
        webglFingerprint,
        audioFingerprint,
        fonts
    };
    
    // Store fingerprint
    localStorage.setItem(DEVICE_FINGERPRINT_KEY, JSON.stringify(fingerprint));
    
    return fingerprint;
};

/**
 * Gets the stored device fingerprint or generates a new one
 */
export const getDeviceFingerprint = async (): Promise<DeviceFingerprint> => {
    const stored = localStorage.getItem(DEVICE_FINGERPRINT_KEY);
    
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            // Check if stored fingerprint has all required fields
            // If missing new fields, regenerate
            if (parsed.canvasFingerprint && parsed.webglFingerprint && parsed.audioFingerprint && parsed.fonts) {
                return parsed;
            }
            console.log('Stored fingerprint missing new fields, regenerating...');
        } catch (e) {
            console.error('Failed to parse stored fingerprint, generating new one');
        }
    }
    
    return await generateDeviceFingerprint();
};

/**
 * Generates a unique client identifier and stores it in localStorage
 * If one already exists, returns the existing ID
 */
export const getOrCreateClientId = (): string => {
    let clientId = localStorage.getItem(CLIENT_ID_KEY);
    
    if (!clientId) {
        // Generate a unique ID: timestamp + random string
        clientId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem(CLIENT_ID_KEY, clientId);
    }
    
    return clientId;
};

/**
 * Simple hash function for creating fingerprint
 */
const simpleHash = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
};

/**
 * Parses user agent to extract readable browser and OS info
 */
export const parseUserAgent = (userAgent: string): { browser: string; os: string } => {
    let browser = 'Unknown';
    let os = 'Unknown';
    
    // Detect OS
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac OS')) os = 'MacOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';
    
    // Detect Browser
    if (userAgent.includes('Edg')) browser = 'Edge';
    else if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
    
    return { browser, os };
};

/**
 * Gets a human-readable device description
 */
export const getDeviceDescription = (fingerprint: DeviceFingerprint): string => {
    const parts = [
        fingerprint.platform,
        fingerprint.screenResolution,
        `${fingerprint.hardwareConcurrency} cores`,
        fingerprint.deviceMemory ? `${fingerprint.deviceMemory}GB RAM` : null
    ].filter(Boolean);
    
    return parts.join(' | ');
};
